/**
 * Versioned FlexLayout envelope migration, validation and filtering
 * (ADR-UI-003 §4). Same chained-versioned-migration-with-fallback shape as
 * the retired `../migration.ts`, ported to walk `IJsonModel`'s `layout`/
 * `borders` trees instead of a flat panel array. There is no prior
 * FlexLayout-shaped persisted data, so `CURRENT_SCHEMA_VERSION` is 1 and the
 * migration chain is empty — it is still built generically so a future
 * schema bump has somewhere to add a step.
 */
import type {
  IJsonBorderNode,
  IJsonModel,
  IJsonRowNode,
  IJsonTabNode,
  IJsonTabSetNode,
} from "flexlayout-react";
import type { PermissionCode } from "../../app/generated/shell-permission-codes";
import { isWorkspaceComponentId } from "../registry/component-registry";
import type { WorkspaceComponentRegistry } from "../registry/component-registry";
import type { DeviceMode, WorkspaceComponentId, WorkspaceLayoutEnvelope } from "../types";

export const CURRENT_SCHEMA_VERSION = 2;

export type EnvelopeMigrationStep = (envelope: Record<string, unknown>) => Record<string, unknown>;

/** Each entry upgrades an envelope from version `N` to `N + 1`. */
export const envelopeMigrations: Readonly<Record<number, EnvelopeMigrationStep>> = {
  // Version 2 scopes storage keys by role/perspective at the caller. The model
  // shape itself is unchanged, so old envelopes remain safely recoverable.
  1: (envelope) => ({ ...envelope, schemaVersion: 2 }),
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isDeviceMode(value: unknown): value is DeviceMode {
  return value === "desktop" || value === "tablet" || value === "mobile";
}

/**
 * The chain runner, parameterised over an explicit `migrations`/`currentVersion`
 * pair rather than closing over the module constants directly, so the
 * "gap in the chain" fallback (a future schema bump that forgets a step) is
 * deterministically unit-testable today, even though `envelopeMigrations` is
 * empty and `CURRENT_SCHEMA_VERSION` is 1 for this brand-new schema.
 */
export function applyMigrationChain(
  raw: unknown,
  migrations: Readonly<Record<number, EnvelopeMigrationStep>>,
  currentVersion: number,
): Record<string, unknown> | null {
  if (!isObject(raw)) return null;
  const version = raw["schemaVersion"];
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1) return null;
  if (version > currentVersion) return null;

  let current: Record<string, unknown> = raw;
  let at = version;
  while (at < currentVersion) {
    const step = migrations[at];
    if (step === undefined) return null;
    const next = step(current);
    if (!isObject(next)) return null;
    current = next;
    at += 1;
  }
  return current;
}

function runMigrationChain(raw: unknown): Record<string, unknown> | null {
  return applyMigrationChain(raw, envelopeMigrations, CURRENT_SCHEMA_VERSION);
}

/** Structural check only: schema version, preset id, device mode and a `model.layout` object are present. */
export function validateEnvelope(raw: unknown): WorkspaceLayoutEnvelope | null {
  const migrated = runMigrationChain(raw);
  if (migrated === null) return null;
  if (migrated["schemaVersion"] !== CURRENT_SCHEMA_VERSION) return null;

  const presetId = migrated["presetId"];
  if (typeof presetId !== "string" || presetId === "") return null;

  const deviceMode = migrated["deviceMode"];
  if (!isDeviceMode(deviceMode)) return null;

  const model = migrated["model"];
  if (!isObject(model) || !isObject(model["layout"])) return null;

  return { schemaVersion: CURRENT_SCHEMA_VERSION, presetId, deviceMode, model };
}

function isRowNodeJson(value: unknown): value is IJsonRowNode {
  return isObject(value) && value["type"] === "row";
}

function isTabSetNodeJson(value: unknown): value is IJsonTabSetNode {
  return isObject(value) && value["type"] === "tabset";
}

function filterTabNodes(
  tabs: readonly IJsonTabNode[] | undefined,
  isAllowed: (tab: IJsonTabNode) => boolean,
): IJsonTabNode[] {
  return (tabs ?? []).filter(isAllowed);
}

function filterTabSetNode(
  tabSet: IJsonTabSetNode,
  isAllowed: (tab: IJsonTabNode) => boolean,
): IJsonTabSetNode | null {
  const children = filterTabNodes(tabSet.children, isAllowed);
  if (children.length === 0) return null;
  return { ...tabSet, children };
}

function filterRowNode(
  row: IJsonRowNode,
  isAllowed: (tab: IJsonTabNode) => boolean,
): IJsonRowNode | null {
  const filteredChildren: (IJsonRowNode | IJsonTabSetNode)[] = [];
  for (const child of row.children ?? []) {
    if (isTabSetNodeJson(child)) {
      const filtered = filterTabSetNode(child, isAllowed);
      if (filtered !== null) filteredChildren.push(filtered);
    } else if (isRowNodeJson(child)) {
      const filtered = filterRowNode(child, isAllowed);
      if (filtered !== null) filteredChildren.push(filtered);
    }
  }
  if (filteredChildren.length === 0) return null;
  return { ...row, children: filteredChildren };
}

function filterBorderNode(
  border: IJsonBorderNode,
  isAllowed: (tab: IJsonTabNode) => boolean,
): IJsonBorderNode {
  const originalChildren = border.children ?? [];
  const children = filterTabNodes(originalChildren, isAllowed);
  const originallySelected =
    border.selected !== undefined && border.selected >= 0
      ? originalChildren[border.selected]
      : undefined;
  const selectedIndex =
    originallySelected === undefined
      ? -1
      : children.findIndex((tab) => tab.id === originallySelected.id);
  const selected =
    children.length === 0
      ? -1
      : selectedIndex >= 0
        ? selectedIndex
        : border.selected === -1
          ? -1
          : 0;
  return { ...border, children, selected };
}

/** Drops every tab (and any tabset/row left with zero children) that fails `isAllowed`. */
export function filterModelByAllowedTabs(
  model: IJsonModel,
  isAllowed: (tab: IJsonTabNode) => boolean,
): IJsonModel {
  const filteredLayout = filterRowNode(model.layout, isAllowed) ?? { ...model.layout, children: [] };
  const filteredBorders = model.borders?.map((border) => filterBorderNode(border, isAllowed));
  return {
    ...model,
    layout: filteredLayout,
    ...(filteredBorders === undefined ? {} : { borders: filteredBorders }),
  };
}

/** Whitelist filter: drops any tab node whose `component` is not a known, registered `WorkspaceComponentId`. */
export function applyRegistryWhitelist(model: IJsonModel, registry: WorkspaceComponentRegistry): IJsonModel {
  return filterModelByAllowedTabs(model, (tab) => {
    const component = tab.component;
    return component !== undefined && isWorkspaceComponentId(component) && registry.has(component);
  });
}

/**
 * Permission filter: additionally drops any surviving tab whose definition's
 * `requiredPermissions` the current session does not hold. Re-run on every
 * restore (not only at save time), so a layout saved before a permission was
 * revoked cannot regrant access.
 */
export function applyPermissionFilter(
  model: IJsonModel,
  registry: WorkspaceComponentRegistry,
  canDiscover: (required: readonly PermissionCode[]) => boolean,
): IJsonModel {
  return filterModelByAllowedTabs(model, (tab) => {
    const component = tab.component;
    if (component === undefined || !isWorkspaceComponentId(component)) return false;
    const definition = registry.get(component);
    if (definition === undefined) return false;
    return canDiscover(definition.requiredPermissions);
  });
}

/**
 * Removes panels that are authorized in the abstract but irrelevant to the
 * active navigation perspective. This is deliberately a separate pass from
 * permissions: mission_read does not make every mission panel useful to every
 * role, and presentation filtering must never be mistaken for authorization.
 */
export function applyPerspectiveRelevanceFilter(
  model: IJsonModel,
  isRelevant: (component: WorkspaceComponentId) => boolean,
): IJsonModel {
  return filterModelByAllowedTabs(model, (tab) => {
    const component = tab.component;
    return component !== undefined &&
      isWorkspaceComponentId(component) &&
      isRelevant(component);
  });
}

function countTabs(model: IJsonModel): number {
  let count = 0;
  const visitTabs = (tabs: readonly IJsonTabNode[] | undefined): void => {
    count += tabs?.length ?? 0;
  };
  const visitRow = (row: IJsonRowNode): void => {
    for (const child of row.children ?? []) {
      if (isTabSetNodeJson(child)) visitTabs(child.children);
      else if (isRowNodeJson(child)) visitRow(child);
    }
  };
  visitRow(model.layout);
  for (const border of model.borders ?? []) visitTabs(border.children);
  return count;
}

/**
 * The full restore path (ADR-UI-003 §4, 5 mandatory steps): structural
 * validation, migration, whitelist filter, permission filter, then a
 * zero-tabs check — falling back to the caller-supplied role default on any
 * failure, exactly like the old `migrateLayout`'s fallback contract.
 */
export function restoreWorkspaceLayout(
  raw: unknown,
  fallback: WorkspaceLayoutEnvelope,
  registry: WorkspaceComponentRegistry,
  canDiscover: (required: readonly PermissionCode[]) => boolean,
  isRelevant?: (component: WorkspaceComponentId) => boolean,
): WorkspaceLayoutEnvelope {
  try {
    const envelope = validateEnvelope(raw);
    if (envelope === null) return fallback;
    const model = envelope.model as IJsonModel;
    const whitelisted = applyRegistryWhitelist(model, registry);
    const permitted = applyPermissionFilter(whitelisted, registry, canDiscover);
    const relevant = isRelevant === undefined
      ? permitted
      : applyPerspectiveRelevanceFilter(permitted, isRelevant);
    if (countTabs(relevant) === 0) return fallback;
    return { ...envelope, model: relevant };
  } catch {
    return fallback;
  }
}
